import { Link, Outlet, useNavigate } from "react-router-dom";
import { Default } from "./default";
import "../../css/userDashboard.css";
import "../../css/modal.css";
import { useEffect, useState } from "react";
import { axiosAuth } from "../../utils/axiosInstance";
import Cookies from "js-cookie";

const UserDashboard = () => {
  const [fullName, setFullName] = useState("Loading...");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null); // { type: 'success'|'warn'|'error', message: string }
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosAuth.get("get-user-details");
        if (response.status === 200 && response.data?.user?.name) {
          setFullName(response.data.user.name);
        } else {
          setFullName("User");
        }
      } catch (error) {
        console.error("Error fetching user name:", error);
        setFullName("User");
      }
    };

    fetchUserData();
  }, []);

  // Delete Account Flow (custom modal instead of window.alert/confirm)
  const handleDeleteAccount = () => {
    setDeleteResult(null);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAccount = async () => {
    setDeletingAccount(true);
    setDeleteResult(null);
    try {
      const bookingsResponse = await axiosAuth.get("my-bookings");
      if (
        bookingsResponse.status === 200 &&
        Array.isArray(bookingsResponse.data)
      ) {
        const activeBookings = bookingsResponse.data.filter(
          (booking) =>
            booking.status === "pending" || booking.status === "confirmed",
        );
        if (activeBookings.length > 0) {
          setDeleteResult({
            type: "warn",
            message:
              "Cancel your active bookings before deleting your account.",
          });
          return;
        }
      }

      const response = await axiosAuth.delete("/delete-account");
      if (response.status === 200) {
        setDeleteResult({
          type: "success",
          message: "Your account has been deleted.",
        });

        Cookies.remove("USER_TOKEN");
        localStorage.removeItem("isProfileSaved");
        localStorage.removeItem("loginType");
        localStorage.removeItem("isPhoneNumberSet");

        // Allow the user to briefly see the message.
        setTimeout(() => navigate("/"), 900);
      } else {
        setDeleteResult({
          type: "error",
          message: "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteResult({
        type: "error",
        message:
          "Unable to delete your account right now. Please try again.",
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <Default>
      <div className="dashbord-usr">
        <div
          className="container-fluid px-lg-5"
          style={{ paddingTop: "100px" }}
        >
          <div className="row flex-column flex-md-row">
            <div className="col-12 col-md-4 col-lg-3 mb-3 mb-md-0">
              <aside className="aside-usr">
                <div className="aside-detail-box-usr">
                  <div className="d-flex justify-content-center mb-2">
                    <span className="aside-detail-box-icon-usr">
                      {/* User Icon */}
                      <svg
                        width="21"
                        height="20"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {/* Add your icon path here */}
                      </svg>
                    </span>
                  </div>
                  <p className="upload-heading text-center">{fullName}</p>
                </div>

                {/* Sidebar Navigation */}
                <ul>
                  <li>
                    <Link to="/user/profile" className="upload-heading">
                      <span className="me-2">{/* Profile icon */}</span>
                      Edit your Profile
                    </Link>
                  </li>
                  <li>
                    <Link to="/user/bookings" className="upload-heading">
                      <span className="me-2">{/* Booking icon */}</span>
                      All Bookings
                    </Link>
                  </li>
                  <li className="text-center mt-3">
                    <button
                      className="btn btn-outline-danger"
                      onClick={handleDeleteAccount}
                    >
                      <b>Delete Your Account</b>
                    </button>
                  </li>
                </ul>
              </aside>
            </div>

            {/* Main Content */}
            <div className="col-12 col-md-8 col-lg-9">
              <div className="aside-outlet-wrapper-usr">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteConfirmOpen ? (
        <dialog
          className="ua-confirm-overlay"
          open
          aria-modal="true"
          aria-labelledby="delete-account-title"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !deletingAccount) {
              setDeleteConfirmOpen(false);
              setDeleteResult(null);
            }
          }}
          onClick={(e) => {
            // Only close when clicking the backdrop, not the modal content.
            if (
              !deletingAccount &&
              e.target === e.currentTarget
            ) {
              setDeleteConfirmOpen(false);
              setDeleteResult(null);
            }
          }}
        >
          <div
            className="ua-confirm-modal"
          >
            {deleteResult ? (
              <>
                <h3
                  id="delete-account-title"
                  className="ua-confirm-modal__title"
                >
                  {deleteResult.type === "success"
                    ? "Account deleted"
                    : deleteResult.type === "warn"
                      ? "Action needed"
                      : "Delete failed"}
                </h3>
                <p className="ua-confirm-modal__message">{deleteResult.message}</p>
                <div className="ua-confirm-modal__actions">
                  <button
                    type="button"
                    className={
                      deleteResult.type === "success"
                        ? "btn btn-primary"
                        : "btn btn-outline-secondary"
                    }
                    onClick={() => {
                      if (deleteResult.type === "success") navigate("/");
                      else {
                        setDeleteConfirmOpen(false);
                        setDeleteResult(null);
                      }
                    }}
                  >
                    {deleteResult.type === "success" ? "OK" : "Close"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3
                  id="delete-account-title"
                  className="ua-confirm-modal__title"
                >
                  Delete account
                </h3>
                <p className="ua-confirm-modal__message">
                  Are you sure you want to delete your account? This action
                  cannot be undone.
                </p>
                <div className="ua-confirm-modal__actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setDeleteConfirmOpen(false);
                      setDeleteResult(null);
                    }}
                    disabled={deletingAccount}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={confirmDeleteAccount}
                    disabled={deletingAccount}
                  >
                    {deletingAccount ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </>
            )}
          </div>
        </dialog>
      ) : null}
    </Default>
  );
};

export default UserDashboard;
